import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { getGeneration, recoverGeneration } from '../api/client';
import type { GenerationDetail, GenerationStatus, SocialMediaOutput } from '../api/types';
import { ApiClientError } from '../api/types';
import '../components/ui/ui-button';
import { alertStyles, motionStyles, boxSizingStyles, responsiveStyles } from '../styles/lit-shared';
import { navigateBack } from '../router';
import { markdownToHtml } from '../lib/markdown';
import { copyPlainText, copyRichHtml, htmlForClipboard } from '../lib/clipboard';
import { resolveGenerationImageUrl } from '../lib/images';

type CopyMenuPosition = 'top' | 'bottom';
type ArticleCopyFormat = 'md' | 'json' | 'rich';

const STATUS_CONFIG: Record<GenerationStatus, { label: string; class: string }> = {
  pending: { label: 'En cola', class: 'badge--pending' },
  processing: { label: 'Procesando con Blunticle…', class: 'badge--processing' },
  completed: { label: 'Completado', class: 'badge--completed' },
  failed: { label: 'Error', class: 'badge--failed' },
};

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

@customElement('content-detail-page')
export class ContentDetailPage extends LitElement {
  static styles = [
    boxSizingStyles,
    responsiveStyles,
    motionStyles,
    alertStyles,
    css`
      :host { display: block; }

      .back-link {
        display: inline-flex;
        align-items: center;
        gap: var(--space-xs);
        margin-bottom: var(--space-xl);
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--color-text-muted);
        cursor: pointer;
        border: none;
        background: transparent;
        padding: 0;
        transition: color var(--duration-fast) var(--ease-out);
      }

      .back-link:hover { color: var(--color-primary-light); }

      /* Header */
      .page-header {
        margin-bottom: var(--space-2xl);
        padding: var(--space-xl);
        border-radius: var(--radius-lg);
        background: var(--gradient-primary-soft);
        border: 1px solid var(--color-border-glow);
        position: relative;
        overflow: hidden;
      }

      .page-header::before {
        content: '';
        position: absolute;
        top: -40%;
        right: -5%;
        width: 220px;
        height: 220px;
        border-radius: 50%;
        background: var(--glow-violet);
        filter: blur(45px);
        pointer-events: none;
      }

      .header-content { position: relative; z-index: 1; }

      .header-meta {
        display: flex;
        align-items: center;
        gap: var(--space-sm);
        flex-wrap: wrap;
        margin-bottom: var(--space-sm);
      }

      .page-title {
        font-size: clamp(1.125rem, 2.5vw, 1.5rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        word-break: break-word;
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        margin-bottom: var(--space-xs);
      }

      .page-description {
        font-size: 0.9375rem;
        color: var(--color-text-muted);
        line-height: 1.6;
        margin-bottom: var(--space-sm);
        max-width: 72ch;
      }

      .page-url {
        font-size: 0.8125rem;
        color: var(--color-text-subtle);
        word-break: break-all;
        margin-bottom: var(--space-xs);
      }

      .header-date {
        font-size: 0.8125rem;
        color: var(--color-text-muted);
      }

      /* Badges */
      .badge {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        padding: 0.2rem 0.6rem;
        border-radius: var(--radius-full);
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.03em;
      }

      .badge-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        flex-shrink: 0;
      }

      .badge--pending {
        background: rgb(251 191 36 / 12%);
        color: var(--color-warning);
        border: 1px solid rgb(251 191 36 / 25%);
      }

      .badge--pending .badge-dot {
        background: var(--color-warning);
        animation: pulse-glow 2s ease-in-out infinite;
      }

      .badge--processing {
        background: rgb(139 92 246 / 12%);
        color: var(--color-primary-light);
        border: 1px solid rgb(139 92 246 / 25%);
      }

      .badge--processing .badge-dot {
        background: var(--color-primary-light);
        animation: pulse-glow 1.2s ease-in-out infinite;
      }

      .badge--completed {
        background: rgb(52 211 153 / 12%);
        color: var(--color-success);
        border: 1px solid rgb(52 211 153 / 25%);
      }

      .badge--completed .badge-dot { background: var(--color-success); }

      .badge--failed {
        background: rgb(251 113 133 / 12%);
        color: var(--color-error);
        border: 1px solid rgb(251 113 133 / 25%);
      }

      .badge--failed .badge-dot { background: var(--color-error); }

      /* Section */
      .section {
        margin-bottom: var(--space-2xl);
      }

      .section-header {
        display: flex;
        align-items: center;
        gap: var(--space-sm);
        margin-bottom: var(--space-lg);
      }

      .section-title {
        font-size: 1.125rem;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--color-text);
      }

      .section-header::after {
        content: '';
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, var(--color-border-strong) 0%, transparent 100%);
      }

      /* Article */
      .article-body {
        padding: var(--space-xl);
        border-radius: var(--radius);
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        line-height: 1.75;
        font-size: 0.9375rem;
        color: var(--color-text);
        overflow-wrap: anywhere;
        word-break: break-word;
      }

      .article-body h1 { font-size: 1.5rem; font-weight: 700; margin: 0 0 var(--space-md); letter-spacing: -0.02em; }
      .article-body h2 { font-size: 1.125rem; font-weight: 700; margin: var(--space-xl) 0 var(--space-sm); color: var(--color-primary-light); }
      .article-body h3 { font-size: 1rem; font-weight: 600; margin: var(--space-lg) 0 var(--space-xs); }
      .article-body p { margin: 0 0 var(--space-md); }
      .article-body ul { padding-left: var(--space-lg); margin: 0 0 var(--space-md); }
      .article-body li { margin-bottom: var(--space-xs); }

      .article-body .md-table-wrap {
        overflow-x: auto;
        margin: 0 0 var(--space-lg);
        border-radius: var(--radius-sm);
        border: 1px solid var(--color-border);
      }

      .article-body table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.875rem;
      }

      .article-body th,
      .article-body td {
        padding: 0.625rem 0.875rem;
        border: 1px solid var(--color-border);
        text-align: left;
        vertical-align: top;
      }

      .article-body th {
        background: rgb(139 92 246 / 10%);
        font-weight: 600;
        color: var(--color-primary-light);
        white-space: nowrap;
      }

      .article-body tbody tr:nth-child(even) {
        background: rgb(255 255 255 / 2%);
      }

      .article-body tbody tr:hover {
        background: rgb(139 92 246 / 6%);
      }

      /* Social */
      .social-grid {
        display: flex;
        flex-direction: column;
        gap: var(--space-xl);
      }

      .social-platform-block {
        border-radius: var(--radius);
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        overflow: hidden;
      }

      .social-platform-header {
        display: flex;
        align-items: center;
        gap: var(--space-xs);
        padding: var(--space-md) var(--space-lg);
        border-bottom: 1px solid var(--color-border);
        font-size: 0.8125rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--color-text-muted);
        background: linear-gradient(90deg, var(--color-border) 0%, transparent 100%);
        position: relative;
      }

      .social-platform-header::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--gradient-primary);
        opacity: 0.7;
      }

      .social-variants-row {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0;
      }

      @media (min-width: 768px) {
        .social-variants-row { grid-template-columns: repeat(3, 1fr); }
      }

      .social-card {
        position: relative;
        padding: var(--space-lg);
        border-right: 1px solid var(--color-border);
        transition: background var(--duration-normal) var(--ease-out);
      }

      .social-card:last-child { border-right: none; }

      @media (max-width: 767px) {
        .social-card {
          border-right: none;
          border-bottom: 1px solid var(--color-border);
        }
        .social-card:last-child { border-bottom: none; }
      }

      .social-card:hover { background: rgb(139 92 246 / 4%); }

      .social-variant-label {
        font-size: 0.6875rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-primary-light);
        margin-bottom: var(--space-xs);
        opacity: 0.8;
      }

      .social-text {
        font-size: 0.9375rem;
        line-height: 1.65;
        color: var(--color-text);
        white-space: pre-wrap;
        word-break: break-word;
      }

      .copy-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        margin-top: var(--space-sm);
        padding: 0.25rem 0.6rem;
        border: 1px solid var(--color-border-strong);
        border-radius: var(--radius-sm);
        background: transparent;
        color: var(--color-text-muted);
        font-size: 0.75rem;
        font-weight: 500;
        cursor: pointer;
        transition:
          border-color var(--duration-fast) var(--ease-out),
          color var(--duration-fast) var(--ease-out);
      }

      .copy-btn:hover {
        border-color: var(--color-primary-light);
        color: var(--color-primary-light);
      }

      /* Article copy dropdown */
      .article-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-sm);
        flex-wrap: wrap;
        margin-bottom: var(--space-lg);
      }

      .article-toolbar--bottom {
        margin-top: var(--space-lg);
        margin-bottom: 0;
        justify-content: flex-end;
      }

      .article-toolbar .section-title {
        margin: 0;
      }

      .article-toolbar .section-header {
        flex: 1;
        margin-bottom: 0;
        min-width: 0;
      }

      .copy-menu {
        position: relative;
        flex-shrink: 0;
      }

      .copy-menu-trigger {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        padding: 0.35rem 0.75rem;
        border: 1px solid var(--color-border-strong);
        border-radius: var(--radius-sm);
        background: var(--color-surface);
        color: var(--color-text-muted);
        font-size: 0.8125rem;
        font-weight: 500;
        cursor: pointer;
        transition:
          border-color var(--duration-fast) var(--ease-out),
          color var(--duration-fast) var(--ease-out);
      }

      .copy-menu-trigger:hover,
      .copy-menu-trigger[aria-expanded='true'] {
        border-color: var(--color-primary-light);
        color: var(--color-primary-light);
      }

      .copy-menu-trigger .chevron {
        font-size: 0.65rem;
        opacity: 0.8;
      }

      .copy-menu-panel {
        position: absolute;
        top: calc(100% + 0.35rem);
        right: 0;
        z-index: 20;
        min-width: 12.5rem;
        padding: 0.35rem;
        border-radius: var(--radius-sm);
        border: 1px solid var(--color-border-strong);
        background: black;
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        box-shadow: 0 8px 24px rgb(0 0 0 / 25%);
      }

      .copy-menu-panel--up {
        top: auto;
        bottom: calc(100% + 0.35rem);
      }

      .copy-menu-item {
        display: block;
        width: 100%;
        text-align: left;
        padding: 0.5rem 0.65rem;
        border: none;
        border-radius: calc(var(--radius-sm) - 2px);
        background: transparent;
        color: var(--color-text);
        font-size: 0.8125rem;
        font-weight: 500;
        cursor: pointer;
        transition: background var(--duration-fast) var(--ease-out);
      }

      .copy-menu-item:hover:not(:disabled) {
        background: rgb(139 92 246 / 12%);
        color: var(--color-primary-light);
      }

      .copy-menu-item:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }

      .copy-menu-item small {
        display: block;
        margin-top: 0.15rem;
        font-size: 0.6875rem;
        font-weight: 400;
        color: var(--color-text-subtle);
      }

      /* Image */
      .generation-image {
        width: 100%;
        border-radius: var(--radius);
        border: 1px solid var(--color-border);
        object-fit: cover;
      }

      /* Waiting */
      .waiting-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--space-lg);
        padding: var(--space-2xl);
        text-align: center;
        border-radius: var(--radius);
        background: var(--color-surface);
        border: 1px solid var(--color-border);
      }

      .waiting-state p {
        color: var(--color-text-muted);
        max-width: 36ch;
      }

      .waiting-state .hint {
        font-size: 0.8125rem;
        color: var(--color-text-subtle);
      }

      /* Loading / error */
      .loading-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--space-md);
        padding: var(--space-2xl) 0;
        color: var(--color-text-muted);
      }

      .error-state {
        padding: var(--space-lg);
        border-radius: var(--radius);
        background: rgb(244 63 94 / 8%);
        border: 1px solid rgb(251 113 133 / 25%);
        color: var(--color-error);
      }

      .notice-state {
        padding: var(--space-lg);
        border-radius: var(--radius);
        background: rgb(251 191 36 / 8%);
        border: 1px solid rgb(251 191 36 / 25%);
        color: var(--color-warning);
        margin-bottom: var(--space-xl);
      }

      .recover-actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-md);
        margin-top: var(--space-md);
      }

      @media (max-width: 639px) {
        .page-header {
          padding: var(--space-lg);
        }

        .article-body {
          padding: var(--space-lg);
        }
      }
    `,
  ];

  @property({ type: String }) generationId = '';
  @state() private detail: GenerationDetail | null = null;
  @state() private loading = true;
  @state() private error = '';
  @state() private copiedKey = '';
  @state() private recovering = false;
  @state() private copyMenuOpen: CopyMenuPosition | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private copiedResetTimer: ReturnType<typeof setTimeout> | null = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleDocumentKeydown);
    void this.load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.clearPoll();
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
    if (this.copiedResetTimer !== null) {
      clearTimeout(this.copiedResetTimer);
      this.copiedResetTimer = null;
    }
  }

  private readonly handleDocumentClick = (e: MouseEvent) => {
    if (this.copyMenuOpen === null) return;
    const path = e.composedPath();
    const hitMenu = path.some(
      (node) => node instanceof Element && node.classList?.contains('copy-menu'),
    );
    if (!hitMenu) this.copyMenuOpen = null;
  };

  private readonly handleDocumentKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.copyMenuOpen !== null) {
      this.copyMenuOpen = null;
    }
  };

  private clearPoll() {
    if (this.pollTimer !== null) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private async load(showSpinner = true) {
    this.clearPoll();
    if (showSpinner) {
      this.loading = true;
      this.error = '';
    }
    try {
      this.detail = await getGeneration(this.generationId);
      if (this.detail.status === 'pending' || this.detail.status === 'processing') {
        this.pollTimer = setTimeout(() => void this.load(false), 5000);
      }
    } catch (err) {
      this.error =
        err instanceof ApiClientError ? err.message : 'Error al cargar el detalle.';
    } finally {
      if (showSpinner) this.loading = false;
    }
  }

  private async copyText(text: string, key: string) {
    try {
      await copyPlainText(text);
      this.markCopied(key);
    } catch {
      // ignore
    }
  }

  private markCopied(key: string) {
    this.copiedKey = key;
    if (this.copiedResetTimer !== null) clearTimeout(this.copiedResetTimer);
    this.copiedResetTimer = setTimeout(() => {
      this.copiedKey = '';
      this.copiedResetTimer = null;
    }, 2000);
  }

  private toggleCopyMenu(position: CopyMenuPosition, e: Event) {
    e.stopPropagation();
    this.copyMenuOpen = this.copyMenuOpen === position ? null : position;
  }

  private async copyArticle(format: ArticleCopyFormat) {
    const d = this.detail;
    if (!d) return;

    try {
      if (format === 'md') {
        if (!d.blogArticle) return;
        await copyPlainText(d.blogArticle);
        this.markCopied('article-md');
      } else if (format === 'json') {
        if (!d.contentArticle) return;
        await copyPlainText(JSON.stringify(d.contentArticle, null, 2));
        this.markCopied('article-json');
      } else {
        if (!d.blogArticle) return;
        const html = htmlForClipboard(d.blogArticle);
        await copyRichHtml(html, d.blogArticle);
        this.markCopied('article-rich');
      }
      this.copyMenuOpen = null;
    } catch {
      // ignore
    }
  }

  private renderCopyMenu(position: CopyMenuPosition) {
    const open = this.copyMenuOpen === position;
    const justCopied =
      this.copiedKey === 'article-md' ||
      this.copiedKey === 'article-json' ||
      this.copiedKey === 'article-rich';
    const hasJson = Boolean(this.detail?.contentArticle);

    return html`
      <div class="copy-menu">
        <button
          type="button"
          class="copy-menu-trigger"
          aria-haspopup="menu"
          aria-expanded=${open ? 'true' : 'false'}
          @click=${(e: Event) => this.toggleCopyMenu(position, e)}
        >
          ${justCopied && !open ? '✓ Copiado' : 'Copiar Contenido'}
          <span class="chevron" aria-hidden="true">${open ? '▴' : '▾'}</span>
        </button>
        ${open
          ? html`
            <div
              class="copy-menu-panel ${position === 'bottom' ? 'copy-menu-panel--up' : ''}"
              role="menu"
              @click=${(e: Event) => e.stopPropagation()}
            >
              <button
                type="button"
                class="copy-menu-item"
                role="menuitem"
                @click=${() => void this.copyArticle('md')}
              >
                Markdown
                <small>Texto .md</small>
              </button>
              <button
                type="button"
                class="copy-menu-item"
                role="menuitem"
                ?disabled=${!hasJson}
                @click=${() => void this.copyArticle('json')}
              >
                JSON
                <small>Estructura del artículo</small>
              </button>
              <button
                type="button"
                class="copy-menu-item"
                role="menuitem"
                @click=${() => void this.copyArticle('rich')}
              >
                Rich Text (WordPress)
                <small>Pegar con formato</small>
              </button>
            </div>
          `
          : ''}
      </div>
    `;
  }

  private async handleRecover() {
    this.recovering = true;
    try {
      await recoverGeneration(this.generationId);
      await this.load(false);
    } catch (err) {
      this.error =
        err instanceof ApiClientError
          ? err.message
          : 'No se pudo recuperar el contenido.';
    } finally {
      this.recovering = false;
    }
  }

  private hasDisplayableContent(d: GenerationDetail): boolean {
    return Boolean(d.blogArticle || d.contentSocial);
  }

  private renderGeneratedSections(
    d: GenerationDetail,
    showMissingImageNotice = false,
  ) {
    const imageSrc = resolveGenerationImageUrl(d.id, d.imageUrl);

    return html`
      ${d.blogArticle
        ? html`
          <section class="section">
            <div class="article-toolbar">
              <div class="section-header">
                <span class="section-title">Artículo de blog</span>
              </div>
              ${this.renderCopyMenu('top')}
            </div>
            <div
              class="article-body"
              .innerHTML=${markdownToHtml(d.blogArticle!)}
            ></div>
            <div class="article-toolbar article-toolbar--bottom">
              ${this.renderCopyMenu('bottom')}
            </div>
          </section>
        `
        : ''}

      ${d.contentSocial
        ? html`
          <section class="section">
            <div class="section-header">
              <span class="section-title">Contenido social</span>
            </div>
            <div class="social-grid">
              ${this.renderPlatformBlock('Instagram', '📸', d.contentSocial.instagram_posts)}
              ${this.renderPlatformBlock('LinkedIn', '💼', d.contentSocial.linkedin_posts)}
              ${this.renderPlatformBlock('Facebook', '📘', d.contentSocial.facebook_posts)}
              ${this.renderPlatformBlock('X / Twitter', '✦', d.contentSocial.x_posts)}
            </div>
          </section>
        `
        : ''}

      ${imageSrc
        ? html`
          <section class="section">
            <div class="section-header">
              <span class="section-title">Imagen generada</span>
            </div>
            <img
              class="generation-image"
              src=${imageSrc}
              alt="Imagen generada"
              loading="lazy"
            />
          </section>
        `
        : showMissingImageNotice
          ? html`
            <div class="notice-state">
              Imagen no disponible. El artículo y las redes se generaron correctamente.
            </div>
          `
          : ''}
    `;
  }

  private renderBadge(status: GenerationStatus) {
    const cfg = STATUS_CONFIG[status];
    return html`
      <span class="badge ${cfg.class}">
        <span class="badge-dot"></span>
        ${cfg.label}
      </span>
    `;
  }

  private renderPlatformBlock(platform: string, icon: string, posts: string[] | undefined) {
    const variants = Array.isArray(posts) && posts.length > 0 ? posts : [];
    if (variants.length === 0) return html``;
    return html`
      <div class="social-platform-block">
        <div class="social-platform-header">${icon} ${platform}</div>
        <div class="social-variants-row">
          ${variants.map((text, i) => {
            const key = `${platform}-${i}`;
            const isCopied = this.copiedKey === key;
            return html`
              <div class="social-card">
                <p class="social-variant-label">Variante ${i + 1}</p>
                <p class="social-text">${text}</p>
                <button
                  class="copy-btn"
                  @click=${() => void this.copyText(text, key)}
                >
                  ${isCopied ? '✓ Copiado' : '⧉ Copiar'}
                </button>
              </div>
            `;
          })}
        </div>
      </div>
    `;
  }

  private renderContent(d: GenerationDetail) {
    if (d.status === 'pending' || d.status === 'processing') {
      return html`
        <div class="waiting-state">
          <div class="spinner" role="status" aria-label="Procesando"></div>
          <p>
            Blunticle está generando tu contenido. Esto puede tardar unos minutos.
          </p>
          ${d.errorMessage
            ? html`<p class="hint">${d.errorMessage}</p>`
            : html`<span class="hint">
                Esta página se actualiza automáticamente. Se mostrará el contenido cuando esté listo.
              </span>`}
        </div>
      `;
    }

    if (d.status === 'failed') {
      if (this.hasDisplayableContent(d)) {
        return html`
          <div class="notice-state">
            La generación falló en un paso final, pero el contenido principal está disponible.
            ${d.errorMessage ? html`<br /><small>${d.errorMessage}</small>` : ''}
          </div>
          ${this.renderGeneratedSections(d)}
        `;
      }

      return html`
        <div class="error-state alert alert--error">
          ${d.errorMessage ?? 'El proceso de generación falló inesperadamente.'}
          ${d.creditRefunded
            ? html` <br /><small>El crédito ha sido devuelto.</small>`
            : ''}
          <div class="recover-actions">
            <ui-button
              variant="secondary"
              ?loading=${this.recovering}
              ?disabled=${this.recovering}
              @ui-click=${() => void this.handleRecover()}
            >
              Recuperar contenido del roadmap
            </ui-button>
          </div>
        </div>
      `;
    }

    if (d.status !== 'completed' && d.errorMessage) {
      return html`
        <div class="notice-state">${d.errorMessage}</div>
        ${this.renderGeneratedSections(d, true)}
      `;
    }

    return this.renderGeneratedSections(d, d.status === 'completed' && !d.imageUrl);
  }

  render() {
    if (this.loading) {
      return html`
        <div class="loading-state animate-fade-up">
          <div class="spinner" role="status" aria-label="Cargando"></div>
          <p>Cargando generación…</p>
        </div>
      `;
    }

    if (this.error || !this.detail) {
      return html`
        <div class="animate-fade-up">
          <button class="back-link" @click=${() => navigateBack('/content')}>
            ← Volver
          </button>
          <div class="error-state">${this.error || 'Generación no encontrada.'}</div>
        </div>
      `;
    }

    const d = this.detail;
    const cfg = STATUS_CONFIG[d.status];

    return html`
      <div class="animate-fade-up">
        <button class="back-link" @click=${() => navigateBack('/content')}>
          ← Volver
        </button>

        <div class="page-header">
          <div class="header-content">
            <div class="header-meta">
              <span class="badge ${cfg.class}">
                <span class="badge-dot"></span>
                ${cfg.label}
              </span>
            </div>
            <p class="page-title">${d.title || d.productUrl || '—'}</p>
            ${d.description
              ? html`<p class="page-description">${d.description}</p>`
              : ''}
            ${d.productUrl
              ? html`<p class="page-url" title=${d.productUrl}>${d.productUrl}</p>`
              : ''}
            <p class="header-date">Creado el ${formatDate(d.createdAt)}</p>
          </div>
        </div>

        ${this.renderContent(d)}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'content-detail-page': ContentDetailPage;
  }
}
